import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { Box } from '@mui/material';
import { REDUCERS } from '@/constants/reducers';
import axiosApiInstance from '@/api/shared/axios-instance';
import URL_FACTORY from '@/helpers/url-helper';
import { notifyError, notifySuccess } from '@/notifications/notify';

const GoogleOneTapPrompt = ({ user, dispatch }) => {
    const router = useRouter();

    useEffect(() => {
        if (typeof window === 'undefined' || user?.logged) {
            return;
        }

        const STORAGE_KEY = 'google_one_tap_shown';
        const EXPIRATION_TIME = 10 * 60 * 1000; // 10 minutos en milisegundos
        const DELAY_TIME = 5000; // 5 segundos

        // Verificar si ya se mostró recientemente
        const hasBeenShownRecently = () => {
            try {
                const stored = localStorage.getItem(STORAGE_KEY);
                if (!stored) return false;

                const { timestamp } = JSON.parse(stored);
                const now = Date.now();
                const elapsed = now - timestamp;

                // Si ha pasado más de 10 minutos, considerar que expiró
                if (elapsed > EXPIRATION_TIME) {
                    localStorage.removeItem(STORAGE_KEY);
                    return false;
                }

                return true;
            } catch (error) {
                console.error('Error al verificar localStorage:', error);
                return false;
            }
        };

        // Guardar que se mostró
        const markAsShown = () => {
            try {
                const data = {
                    timestamp: Date.now(),
                };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
            } catch (error) {
                console.error('Error al guardar en localStorage:', error);
            }
        };

        // Si ya se mostró recientemente, no hacer nada
        if (hasBeenShownRecently()) {
            return;
        }

        let isCancelled = false;
        let removeScript;

        const runAfterDelay = (callback) => {
            const timeoutId = window.setTimeout(callback, DELAY_TIME);
            return () => window.clearTimeout(timeoutId);
        };

        const loadGoogleSdk = () =>
            new Promise((resolve, reject) => {
                if (typeof window === 'undefined') {
                    reject(new Error('Window is undefined'));
                    return;
                }

                if (window.google?.accounts?.id) {
                    resolve();
                    return;
                }

                const existingScript = document.querySelector('script[data-google-one-tap="true"]');
                if (existingScript) {
                    if (existingScript.getAttribute('data-loaded') === 'true') {
                        resolve();
                    } else {
                        existingScript.addEventListener('load', resolve, { once: true });
                        existingScript.addEventListener('error', reject, { once: true });
                    }
                    return;
                }

                const script = document.createElement('script');
                script.src = 'https://accounts.google.com/gsi/client?hl=es';
                script.async = true;
                script.defer = true;
                script.dataset.googleOneTap = 'true';
                script.addEventListener(
                    'load',
                    () => {
                        script.setAttribute('data-loaded', 'true');
                        resolve();
                    },
                    { once: true }
                );
                script.addEventListener('error', reject, { once: true });
                document.body.appendChild(script);

                removeScript = () => {
                    script.removeEventListener('load', resolve);
                    script.removeEventListener('error', reject);
                    if (document.body.contains(script)) {
                        document.body.removeChild(script);
                    }
                };
            });

        const handleCredentialResponse = async (response) => {
            if (!response?.credential) {
                notifyError('Error al autenticar con Google');
                return;
            }

            dispatch({ type: REDUCERS.SHOW_SPINNER });
            try {
                const result = await axiosApiInstance.post(URL_FACTORY.GoogleLoginUrl, {
                    credential: response.credential,
                });

                if (result?.data) {
                    dispatch({
                        type: REDUCERS.LOGGED_IN_USER,
                        payload: {
                            ...result.data,
                            subscriptionType: result.data.subscriptionType ?? 0,
                            prevUrl: user?.prevUrl,
                        },
                    });

                    notifySuccess(`¡Bienvenido, ${result.data.nickname ?? 'manager'}!`);

                    if (user?.prevUrl) {
                        router.push(user.prevUrl);
                    }
                }
            } catch (error) {
                console.error('Error en Google One Tap:', error);
                notifyError('Error al iniciar sesión con Google. Por favor, inténtalo de nuevo.');
            } finally {
                dispatch({ type: REDUCERS.HIDE_SPINNER });
            }
        };

        const initializeOneTap = async () => {
            try {
                await loadGoogleSdk();
            } catch (error) {
                console.error('No se pudo cargar Google One Tap:', error);
                return;
            }

            if (isCancelled || !window.google?.accounts?.id) {
                return;
            }

            const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

            if (!clientId) {
                console.error('Google Client ID no configurado para One Tap');
                return;
            }

            window.google.accounts.id.initialize({
                client_id: clientId,
                callback: handleCredentialResponse,
                auto_select: false,
                cancel_on_tap_outside: true,
                prompt_parent_id: 'af-google-one-tap-root',
            });

            window.google.accounts.id.prompt((notification) => {
                if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                    console.info('Google One Tap no se mostró:', notification.getNotDisplayedReason?.());
                } else {
                    // Si se mostró, marcar en localStorage
                    markAsShown();
                }
            });
        };

        const cancelDelay = runAfterDelay(initializeOneTap);

        return () => {
            isCancelled = true;
            cancelDelay?.();
            removeScript?.();
            window.google?.accounts?.id?.cancel();
        };
    }, [dispatch, router, user?.logged, user?.prevUrl]);

    return (
        <Box
            id="af-google-one-tap-root"
            sx={{
                position: 'fixed',
                top: 16,
                right: 16,
                zIndex: (theme) => theme.zIndex.modal + 10,
                pointerEvents: 'auto',
            }}
        />
    );
};

export default GoogleOneTapPrompt;